import { useState } from "react";
import { History, AlertTriangle } from "lucide-react";
import { imageUrl } from "@/lib/api";
import { CoverCompare } from "@/components/CoverCompare";

export const SavedCovers = ({ book, onRestore }) => {
  const [picked, setPicked] = useState(null);
  const versions = book.cover_versions || [];
  if (versions.length < 2) return null;
  const name = (v) => (v.original && !/original/i.test(v.label) ? `Original · ${v.label}` : v.label);

  return (
    <div className="border-t border-gold-500/10 pt-4 space-y-3" data-testid="saved-covers">
      <div className="flex items-center gap-2">
        <History className="w-4 h-4 text-gold-300" />
        <p className="font-ui text-sm font-semibold text-slate-100">Saved covers</p>
      </div>
      <p className="font-ui text-xs text-slate-500">Every cover you've had is kept. Compare and switch back any time, free.</p>
      <div className="grid grid-cols-4 gap-2">
        {versions.map((v) => {
          const current = v.image_id === book.cover_image_id;
          return (
            <button key={v.id} data-testid={`saved-cover-${v.id}`} disabled={current}
              onClick={() => setPicked(v)}
              className={`relative rounded-md overflow-hidden border-2 transition-colors ${current ? "border-gold-400" : picked?.id === v.id ? "border-gold-300/80" : "border-transparent hover:border-gold-400/60"}`}>
              <img src={`${imageUrl(v.image_id)}?t=${v.image_id}`} alt={name(v)} className="w-full aspect-[2/3] object-contain bg-navy-950" />
              <span className="absolute top-0 inset-x-0 flex flex-col">
                {v.mismatch && (
                  <span title={v.mismatch} data-testid={`saved-cover-mismatch-${v.id}`}
                    className="flex items-center gap-1 bg-amber-500/90 px-1 py-0.5 font-ui text-[9px] font-semibold text-stone-950 leading-tight">
                    <AlertTriangle className="w-3 h-3 shrink-0" /> Doesn't match this book
                  </span>
                )}
                {v.print_issue && (
                  <span title={v.print_issue} data-testid={`saved-cover-print-issue-${v.id}`}
                    className="flex items-center gap-1 bg-rose-500/90 px-1 py-0.5 font-ui text-[9px] font-semibold text-white leading-tight">
                    <AlertTriangle className="w-3 h-3 shrink-0" /> Not print-ready
                  </span>
                )}
              </span>
              <span className="absolute bottom-0 inset-x-0 bg-navy-950/85 px-1 py-0.5 font-ui text-[10px] text-slate-200 truncate">
                {current ? "Current" : v.original ? "Original" : v.label.split(" · ")[0]}
              </span>
            </button>
          );
        })}
      </div>
      {picked && (
        <CoverCompare book={book} candidate={{ label: name(picked), src: `${imageUrl(picked.image_id)}?t=${picked.image_id}`, warning: picked.mismatch, printIssue: picked.print_issue }}
          actionLabel="Use this cover" onCancel={() => setPicked(null)}
          onConfirm={() => { onRestore(picked.id); setPicked(null); }} />
      )}
    </div>
  );
};
